/**
 * Presentation of the step rail (f10.3). Formatting and class mapping only.
 *
 * Status, the current step, completion facts and every capability arrive from
 * `GET /api/cycles/{id}/workflow`. Nothing here decides whether a step may be
 * accepted or applied -- it only turns the server's answer into display text.
 */

import type { Cycle, CycleWorkflow, CycleWorkflowStep } from '@/api/client';

import { MONTH_NAMES } from './cycleView';

export type RailClass = 'is-done' | 'is-stale' | 'is-current' | 'is-todo';

/** The prototype's four rail classes, from status and the server's current step. */
export function railClassFor(step: CycleWorkflowStep, currentStepCode: string | null): RailClass {
  if (step.status.code === 'COMPLETED') return 'is-done';
  if (step.status.code === 'STALE') return 'is-stale';
  if (step.code === currentStepCode) return 'is-current';
  return 'is-todo';
}

function shortMonth(month: number): string {
  return (MONTH_NAMES[month - 1] ?? '').slice(0, 3);
}

/** An ISO timestamp as `17 Sep`, in the viewer's zone. */
export function formatDayMonth(iso: string): string {
  const moment = new Date(iso);
  return `${String(moment.getDate()).padStart(2, '0')} ${shortMonth(moment.getMonth() + 1)}`;
}

function countsText(step: CycleWorkflowStep): string | null {
  const summary = step.summary;
  if (!summary || summary.expected_batches == null) return null;
  if (summary.committed_batches == null) return null;
  return `${summary.committed_batches} of ${summary.expected_batches} committed`;
}

function joinMeta(parts: (string | null | undefined)[]): string {
  return parts.filter((part): part is string => Boolean(part)).join(' · ');
}

/** Numbers of the preparation steps release needs (f10.9); step 7 (f14.3) comes after release. */
const PREPARATION_STEP_COUNT = 5;

/** Whether every one of steps 1-5 is Completed (step 6's precondition text). */
export function preparationComplete(workflow: CycleWorkflow): boolean {
  return workflow.steps
    .filter((step) => step.number <= PREPARATION_STEP_COUNT)
    .every((step) => step.status.code === 'COMPLETED');
}

/** The meta line under a step title, as the prototype writes it. */
export function stepMeta(step: CycleWorkflowStep, workflow: CycleWorkflow): string {
  const counts = countsText(step);
  const text = step.summary?.text ?? null;
  if (step.status.code === 'STALE') {
    return joinMeta([counts, step.stale?.detail ?? 'Updates available']);
  }
  if (step.status.code === 'COMPLETED') {
    const when = step.completed_at ? formatDayMonth(step.completed_at) : null;
    const who = step.completed_by?.display_name;
    return joinMeta([counts, text, when && who ? `${when}, ${who}` : when]);
  }
  if (step.code === 'RELEASE_FOR_INPUT' && !preparationComplete(workflow)) {
    return 'Needs steps 1–5 committed';
  }
  return joinMeta([step.status.name, counts, text]);
}

/** `badge pill-*` for a step status. */
export function stepPillClass(code: string): string {
  switch (code) {
    case 'COMPLETED':
      return 'badge pill-success';
    case 'STALE':
      return 'badge pill-warning';
    case 'IN_PROGRESS':
      return 'badge pill-info';
    default:
      return 'badge pill-pending';
  }
}

/** `Cycle 2607 · closing July 2026 · open window Aug–Dec 2026`, from `derived`. */
export function cycleSubtitle(cycle: Cycle): string {
  const { closing_month: closing, open_months: open } = cycle.derived;
  const parts = [`Cycle ${cycle.code}`, `closing ${MONTH_NAMES[closing.month - 1]} ${closing.year}`];
  if (open.length) {
    const first = shortMonth(open[0] ?? closing.month);
    const last = shortMonth(open[open.length - 1] ?? closing.month);
    parts.push(`open window ${open.length === 1 ? first : `${first}–${last}`} ${closing.year}`);
  } else {
    parts.push('no open months');
  }
  return parts.join(' · ');
}

/** The stale steps, in number order, for the banner above the grid. */
export function staleSteps(workflow: CycleWorkflow): CycleWorkflowStep[] {
  return workflow.steps.filter((step) => step.status.code === 'STALE');
}

/** How a milestone key reads on the calendar card. */
export const MILESTONE_LABELS: Readonly<Record<string, string>> = {
  PREPARE: 'Prepare',
  OPEN: 'Release for input',
  REVIEW: 'Actuals & review',
  FREEZE: 'Freeze',
  SUBMIT: 'Submit',
};
